import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_PRESENTATION_THEME,
  findThemeById,
} from "../theme/presentation-themes";

const STORAGE_KEY = "@sidekick:presentation-theme-id";

const readValidatedPreference = (): string | null => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  const isValid =
    stored === DEFAULT_PRESENTATION_THEME.id || !!findThemeById(stored);
  if (!isValid) {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
  return stored;
};

type Params = {
  presentationId: string | undefined;
  presentationThemeId: string | null | undefined;
  allowedThemeIds: string[];
  onUpdatePresentationThemeId: (themeId: string) => void;
};

export const usePresentationThemePreference = ({
  presentationId,
  presentationThemeId,
  allowedThemeIds,
  onUpdatePresentationThemeId,
}: Params) => {
  const [storedThemeId, setPreferredThemeId] = useState<string | null>(
    readValidatedPreference,
  );
  const appliedForRef = useRef<string | null>(null);

  const preferredThemeId =
    storedThemeId && allowedThemeIds.includes(storedThemeId)
      ? storedThemeId
      : null;

  useEffect(() => {
    if (!presentationId || presentationThemeId != null) return;
    if (!preferredThemeId) return;
    if (appliedForRef.current === presentationId) return;
    appliedForRef.current = presentationId;
    onUpdatePresentationThemeId(preferredThemeId);
  }, [
    presentationId,
    presentationThemeId,
    preferredThemeId,
    onUpdatePresentationThemeId,
  ]);

  const saveThemePreference = useCallback((themeId: string) => {
    localStorage.setItem(STORAGE_KEY, themeId);
    setPreferredThemeId(themeId);
  }, []);

  return { preferredThemeId, saveThemePreference };
};
